import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Building2, Users, Briefcase, UserRound, Loader2, ZoomIn, ZoomOut,
  Maximize2, Minimize2, ChevronsUpDown, ChevronsDownUp, Landmark, Mail, BadgeCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { api } from '@/lib/api';
import { useDataLabel } from '@/lib/dataI18n';

// Founder V3.1 — vivid accent palette; amber is RESERVED for vacancies.
const ACCENTS = [
  { bar: 'bg-blue-500',    chip: 'bg-blue-50 text-blue-700 border-blue-200',       ring: 'ring-blue-500/60' },
  { bar: 'bg-rose-500',    chip: 'bg-rose-50 text-rose-700 border-rose-200',       ring: 'ring-rose-500/60' },
  { bar: 'bg-orange-500',  chip: 'bg-orange-50 text-orange-700 border-orange-200', ring: 'ring-orange-500/60' },
  { bar: 'bg-yellow-500',  chip: 'bg-yellow-50 text-yellow-700 border-yellow-200', ring: 'ring-yellow-500/60' },
  { bar: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', ring: 'ring-emerald-500/60' },
  { bar: 'bg-violet-500',  chip: 'bg-violet-50 text-violet-700 border-violet-200', ring: 'ring-violet-500/60' },
  { bar: 'bg-cyan-500',    chip: 'bg-cyan-50 text-cyan-700 border-cyan-200',       ring: 'ring-cyan-500/60' },
];

const ZOOM_STEPS = [0.6, 0.75, 0.9, 1, 1.15, 1.3];

export default function AdminOrgBoard({ clientMode = false }) {
  const { t } = useTranslation();
  const tData = useDataLabel();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyId = clientMode ? 'own' : (searchParams.get('company_id') || '');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filterId, setFilterId] = useState('all');
  const [expanded, setExpanded] = useState(false);
  const [zoomIdx, setZoomIdx] = useState(3);
  const [isFs, setIsFs] = useState(false);
  const [posDetail, setPosDetail] = useState(null);
  const [personDetail, setPersonDetail] = useState(null);
  const boardRef = useRef(null);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try { setData(clientMode ? await api.get('/client/org-board').then((r) => r.data) : await adminApi.getOrgBoard(companyId)); }
      catch { toast.error(t('pages.orgBoard.loadFailed', { defaultValue: 'Failed to load org structure' })); }
      finally { setLoading(false); }
    })();
  }, [companyId]); // eslint-disable-line

  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else boardRef.current?.requestFullscreen?.();
  };

  const depts = useMemo(() => {
    const all = data?.departments || [];
    return filterId === 'all' ? all : all.filter((d) => d.id === filterId);
  }, [data, filterId]);

  const selected = useMemo(
    () => (data?.departments || []).find((d) => d.id === selectedId) || null,
    [data, selectedId],
  );

  const accentFor = (dept) => {
    const idx = (data?.departments || []).findIndex((d) => d.id === dept.id);
    return ACCENTS[Math.max(0, idx) % ACCENTS.length];
  };

  const zoom = ZOOM_STEPS[zoomIdx];

  if (!companyId) {
    return (
      <>
        <Topbar title={t('pages.orgBoard.title', { defaultValue: 'Org Structure Board' })} />
        <div className="p-8 text-muted-foreground" data-testid="orgboard-no-company">
          {t('pages.orgBoard.noCompany', { defaultValue: 'Select a client to view its org structure board.' })}
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={t('pages.orgBoard.title', { defaultValue: 'Org Structure Board' })}
        subtitle={data?.company?.name || ''}
      />
      <div className="p-6 lg:p-8 space-y-4" data-testid="orgboard-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          {!clientMode ? (
            <Button variant="ghost" size="sm" className="-ml-2" data-testid="orgboard-back"
                    onClick={() => navigate(`/admin/clients/${companyId}`)}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('pages.orgBoard.backToClient', { defaultValue: 'Back to client' })}
            </Button>
          ) : <span />}

          {/* Toolbar */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="orgboard-toolbar">
            <Select value={filterId} onValueChange={(v) => { setFilterId(v); if (v !== 'all') setSelectedId(v); }}>
              <SelectTrigger className="h-9 w-52" data-testid="orgboard-filter">
                <SelectValue placeholder={t('pages.orgBoard.filterAll', { defaultValue: 'All departments' })} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('pages.orgBoard.filterAll', { defaultValue: 'All departments' })}</SelectItem>
                {(data?.departments || []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>{tData(d)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => setExpanded((e) => !e)} data-testid="orgboard-expand-toggle">
              {expanded
                ? <><ChevronsDownUp className="h-4 w-4 mr-1" />{t('pages.orgBoard.collapseAll', { defaultValue: 'Collapse all' })}</>
                : <><ChevronsUpDown className="h-4 w-4 mr-1" />{t('pages.orgBoard.expandAll', { defaultValue: 'Expand all' })}</>}
            </Button>
            <div className="flex items-center gap-1 border rounded-md px-1 h-9 bg-white">
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={zoomIdx === 0}
                      onClick={() => setZoomIdx((z) => Math.max(0, z - 1))} data-testid="orgboard-zoom-out">
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-xs tabular-nums w-10 text-center" data-testid="orgboard-zoom-level">
                {Math.round(zoom * 100)}%
              </span>
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={zoomIdx === ZOOM_STEPS.length - 1}
                      onClick={() => setZoomIdx((z) => Math.min(ZOOM_STEPS.length - 1, z + 1))} data-testid="orgboard-zoom-in">
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={toggleFullscreen} data-testid="orgboard-fullscreen">
              {isFs
                ? <><Minimize2 className="h-4 w-4 mr-1" />{t('pages.orgBoard.exitFullscreen', { defaultValue: 'Exit full screen' })}</>
                : <><Maximize2 className="h-4 w-4 mr-1" />{t('pages.orgBoard.fullscreen', { defaultValue: 'Full screen' })}</>}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
          </div>
        ) : !data ? null : (
          <div ref={boardRef} className={`space-y-4 ${isFs ? 'bg-background overflow-y-auto p-6' : ''}`} data-testid="orgboard-board">
            <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', width: `${100 / zoom}%` }} className="space-y-4">

              {/* ── Company band ──────────────────────────────────────── */}
              <Card className="p-6 shadow-card" data-testid="orgboard-company-band">
                <div className="flex items-center gap-5 flex-wrap">
                  {data.company.logo_url ? (
                    <img src={data.company.logo_url} alt={data.company.name}
                         className="h-14 max-w-[140px] object-contain shrink-0"
                         data-testid="orgboard-company-logo" />
                  ) : (
                    <div className="h-14 w-14 rounded-lg bg-muted flex items-center justify-center shrink-0"
                         data-testid="orgboard-logo-placeholder">
                      <Building2 className="h-7 w-7 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h2 className="font-display text-2xl font-bold leading-tight" data-testid="orgboard-company-name">
                      {data.company.name}
                    </h2>
                    <p className="text-sm text-muted-foreground">{data.company.country || '—'}</p>
                  </div>
                  <div className="ml-auto flex gap-2 flex-wrap min-w-0 max-w-full justify-end" data-testid="orgboard-totals">
                    <button type="button" data-testid="orgboard-chip-departments"
                            onClick={() => !clientMode && navigate(`/admin/departments?company_id=${companyId}`)}
                            title={t('pages.orgBoard.chipDeptsTitle', { defaultValue: 'Manage departments and formal positions' })}
                            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full">
                      <Badge variant="outline" className="text-sm py-1 px-3 gap-1.5 cursor-pointer hover:bg-muted/60 transition-colors">
                        <Building2 className="h-3.5 w-3.5" />
                        {t('pages.orgBoard.deptCount', { count: data.totals.departments, defaultValue: '{{count}} departments' })}
                      </Badge>
                    </button>
                    <button type="button" data-testid="orgboard-chip-collaborators"
                            onClick={() => !clientMode && navigate(`/admin/users?company=${companyId}`)}
                            title={t('pages.orgBoard.chipCollabTitle', { defaultValue: 'Open collaborators filtered to this client' })}
                            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full">
                      <Badge variant="outline" className="text-sm py-1 px-3 gap-1.5 cursor-pointer hover:bg-muted/60 transition-colors">
                        <Users className="h-3.5 w-3.5" />
                        {t('pages.orgBoard.collabCount', { count: data.totals.collaborators, defaultValue: '{{count}} collaborators' })}
                      </Badge>
                    </button>
                    <button type="button" data-testid="orgboard-chip-vacancies"
                            onClick={() => {
                              if (!data.totals.vacancies) {
                                toast.info(t('pages.orgBoard.noVacancies', { defaultValue: 'No open vacancies at the moment.' }));
                                return;
                              }
                              const withVac = (data.departments || []).find((d) => (d.vacant_positions || 0) > 0);
                              if (withVac) { setFilterId('all'); setSelectedId(withVac.id); }
                            }}
                            title={t('pages.orgBoard.chipVacTitle', { defaultValue: 'Focus the department with open vacancies' })}
                            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full">
                      <Badge variant="outline" className={`text-sm py-1 px-3 gap-1.5 cursor-pointer transition-colors ${data.totals.vacancies > 0 ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' : 'hover:bg-muted/60'}`}>
                        <Briefcase className="h-3.5 w-3.5" />
                        {t('pages.orgBoard.vacancyCount', { count: data.totals.vacancies, defaultValue: '{{count}} vacancies' })}
                      </Badge>
                    </button>
                  </div>
                </div>
              </Card>

              {/* ── Junta Directiva (governance band — no names, ever) ── */}
              <Card className="p-5 shadow-card border-dashed" data-testid="orgboard-junta-band">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                    <Landmark className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-semibold">{t('pages.orgBoard.junta', { defaultValue: 'Junta Directiva' })}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('pages.orgBoard.juntaSub', { defaultValue: 'Governance layer' })}
                    </p>
                  </div>
                </div>
              </Card>

              {/* ── Department board (peer cards, no tree lines) ─────── */}
              {depts.length === 0 ? (
                <Card className="p-10 text-center text-muted-foreground text-sm" data-testid="orgboard-empty">
                  {t('pages.orgBoard.emptyDepts', { defaultValue: 'No departments yet.' })}
                </Card>
              ) : (
                <div className="flex flex-wrap gap-4" data-testid="orgboard-dept-grid">
                  {depts.map((d) => {
                    const acc = accentFor(d);
                    const isSel = d.id === selectedId;
                    return (
                      <Card key={d.id}
                            role="button" tabIndex={0}
                            onClick={() => setSelectedId(isSel ? null : d.id)}
                            onKeyDown={(e) => { if (e.key === 'Enter') setSelectedId(isSel ? null : d.id); }}
                            className={`w-full sm:w-[calc(50%-0.5rem)] xl:w-[calc(33.333%-0.7rem)] 2xl:w-[calc(25%-0.75rem)] min-w-[280px] overflow-hidden cursor-pointer transition-all hover:shadow-lg ${isSel ? `ring-2 ${acc.ring} shadow-lg` : 'shadow-card'}`}
                            data-testid={`orgboard-dept-${d.id}`}>
                        <div className={`h-1.5 ${acc.bar}`} />
                        <div className="p-4 space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold leading-snug">{tData(d)}</h3>
                            {isSel && <BadgeCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" data-testid={`orgboard-dept-selected-${d.id}`} />}
                          </div>
                          <p className="text-sm text-muted-foreground flex items-center gap-1.5 min-w-0">
                            <UserRound className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">
                              {d.manager?.name || t('pages.orgBoard.noResponsible', { defaultValue: 'No responsible assigned' })}
                            </span>
                          </p>
                          <div className="flex flex-wrap gap-1 text-center">
                            <div className={`flex-1 min-w-[96px] max-w-full rounded-md border px-1.5 py-1.5 ${acc.chip}`}>
                              <p className="text-base font-bold tabular-nums leading-none">{d.member_count ?? 0}</p>
                              <p className="text-sm mt-1 leading-tight break-words">{t('pages.orgBoard.collaborators', { count: d.member_count ?? 0, defaultValue: 'Collaborators' })}</p>
                            </div>
                            <div className={`flex-1 min-w-[96px] max-w-full rounded-md border px-1.5 py-1.5 ${acc.chip}`}>
                              <p className="text-base font-bold tabular-nums leading-none">{d.formal_positions_count ?? 0}</p>
                              <p className="text-sm mt-1 leading-tight break-words">{t('pages.orgBoard.positions', { defaultValue: 'Positions' })}</p>
                            </div>
                            <div className={`flex-1 min-w-[96px] max-w-full rounded-md border px-1.5 py-1.5 ${(d.vacant_positions ?? 0) > 0 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-muted/50 text-muted-foreground'}`}>
                              <p className="text-base font-bold tabular-nums leading-none">{d.vacant_positions ?? 0}</p>
                              <p className="text-sm mt-1 leading-tight break-words">{t('pages.orgBoard.vacancies', { defaultValue: 'Vacancies' })}</p>
                            </div>
                          </div>
                          {(d.members_without_position || 0) > 0 && (
                            <p className="text-xs text-amber-700/90" data-testid={`orgboard-unlinked-${d.id}`}>
                              {t('pages.orgBoard.unlinkedHint', { count: d.members_without_position, defaultValue: '{{count}} without a linked formal position' })}
                            </p>
                          )}
                          {expanded && (d.members || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 pt-1" data-testid={`orgboard-dept-members-${d.id}`}>
                              {d.members.slice(0, 8).map((m) => (
                                <button key={m.id} type="button"
                                        onClick={(e) => { e.stopPropagation(); setPersonDetail(m); }}
                                        className="px-2 py-0.5 rounded-full text-xs border bg-white hover:bg-muted transition"
                                        data-testid={`orgboard-member-chip-${m.id}`}>
                                  {m.name}
                                </button>
                              ))}
                              {d.members.length > 8 && (
                                <span className="text-xs text-muted-foreground self-center">+{d.members.length - 8}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* ── Detailed department panel ─────────────────────────── */}
              {selected && (
                <Card className="p-6 shadow-card" data-testid="orgboard-detail-panel">
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <div className={`h-8 w-2 rounded-full ${accentFor(selected).bar}`} />
                    <div>
                      <h3 className="font-display font-bold text-lg">
                        {tData(selected)} — {t('pages.orgBoard.detailTitle', { defaultValue: 'Detailed view' })}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {t('pages.orgBoard.responsible', { defaultValue: 'Responsible' })}:{' '}
                        {selected.manager?.name || t('pages.orgBoard.noResponsible', { defaultValue: 'No responsible assigned' })}
                      </p>
                    </div>
                    {!clientMode && (
                      <Button variant="outline" size="sm" className="ml-auto"
                              onClick={() => navigate(`/admin/departments?company_id=${companyId}&dept=${selected.id}`)}
                              data-testid="orgboard-manage-positions">
                        <Briefcase className="h-4 w-4 mr-1.5" />
                        {t('pages.orgBoard.managePositions', { defaultValue: 'Manage positions' })}
                      </Button>
                    )}
                  </div>

                  {(selected.members_without_position || 0) > 0 && (
                    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/60 px-3.5 py-2.5 text-sm text-amber-900"
                         data-testid="orgboard-detail-unlinked">
                      <p className="font-medium">
                        {t('pages.orgBoard.unlinkedDetail', {
                          count: selected.members_without_position,
                          defaultValue: '{{count}} collaborator(s) in this department are not yet linked to a formal position.',
                        })}
                      </p>
                      <p className="mt-1 text-xs text-amber-800/90">
                        {t('pages.orgBoard.unlinkedConcept', {
                          defaultValue: 'Este colaborador pertenece al departamento, pero todavía no está vinculado a un puesto formal de la estructura. Vincularlo permite relacionar correctamente su rol con los procesos organizacionales que utilizan puestos (vacantes, competencias, desarrollo, sucesión).',
                        })}
                      </p>
                      {(selected.members_unlinked || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="orgboard-unlinked-names">
                          {(selected.members_unlinked || []).map((m) => (
                            <span key={m.id} className="inline-flex items-center rounded-full border border-amber-300 bg-white px-2 py-0.5 text-xs font-medium">
                              {m.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Formal positions */}
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    {t('pages.orgBoard.formalPositions', { defaultValue: 'Formal positions' })}
                  </h4>
                  {(selected.positions_detail || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground italic mb-4" data-testid="orgboard-no-positions">
                      {t('pages.orgBoard.noPositions', { defaultValue: 'No formal positions defined for this department.' })}
                    </p>
                  ) : (
                    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-5" data-testid="orgboard-positions-grid">
                      {selected.positions_detail.map((p) => {
                        const vac = Math.max(0, (p.headcount || 0) - (p.occupied || 0));
                        return (
                          <button key={p.id} type="button" onClick={() => setPosDetail(p)}
                                  className={`text-left rounded-lg border p-3 space-y-2 hover:shadow-md transition ${vac > 0 ? 'border-amber-300 bg-amber-50/50' : 'bg-white'}`}
                                  data-testid={`orgboard-position-${p.id}`}>
                            <p className="font-semibold text-sm">{p.title}</p>
                            <div className="flex gap-3 text-xs text-muted-foreground tabular-nums">
                              <span>{t('pages.orgBoard.authorizedShort', { defaultValue: 'Seats' })}: {p.headcount ?? 0}</span>
                              <span>{t('pages.orgBoard.occupiedShort', { defaultValue: 'Filled' })}: {p.occupied ?? 0}</span>
                              <span className={vac > 0 ? 'text-amber-700 font-semibold' : ''}>
                                {t('pages.orgBoard.vacancies', { defaultValue: 'Vacancies' })}: {vac}
                              </span>
                            </div>
                            {p.role_profile && (
                              <Badge variant="outline" className="text-xs">
                                {t('pages.orgBoard.roleProfile', { defaultValue: 'Role profile' })}: {p.role_profile.position_title || p.role_profile.code}
                              </Badge>
                            )}
                            {vac > 0 && (
                              <p className="text-xs text-amber-700 font-medium" data-testid={`orgboard-vacancy-${p.id}`}>
                                {t('pages.orgBoard.vacancyOpen', { count: vac, defaultValue: '{{count}} open vacancy' })}
                              </p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Occupants / members */}
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    {t('pages.orgBoard.members', { defaultValue: 'Collaborators' })} ({(selected.members || []).length})
                  </h4>
                  {(selected.members || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground italic" data-testid="orgboard-no-members">
                      {t('pages.orgBoard.noMembers', { defaultValue: 'No collaborators in this department.' })}
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2" data-testid="orgboard-members-list">
                      {selected.members.map((m) => (
                        <button key={m.id} type="button" onClick={() => setPersonDetail(m)}
                                className="inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1.5 text-sm hover:bg-muted transition"
                                data-testid={`orgboard-member-${m.id}`}>
                          <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
                            {m.avatar_initials}
                          </span>
                          <span>{m.name}</span>
                          {m.position && <span className="text-xs text-muted-foreground">· {m.position}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </Card>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Position detail dialog */}
      <Dialog open={!!posDetail} onOpenChange={(o) => !o && setPosDetail(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="orgboard-position-dialog">
          {posDetail && (
            <>
              <DialogHeader>
                <DialogTitle>{posDetail.title}</DialogTitle>
                <DialogDescription>
                  {t('pages.orgBoard.positionDetail', { defaultValue: 'Formal position detail' })}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-base">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md border p-3">
                    <p className="text-xl font-bold tabular-nums">{posDetail.headcount ?? 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t('pages.orgBoard.authorizedSeats', { defaultValue: 'Authorized seats' })}</p>
                  </div>
                  <div className="rounded-md border p-3">
                    <p className="text-xl font-bold tabular-nums">{posDetail.occupied ?? 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t('pages.orgBoard.occupiedSeats', { defaultValue: 'Occupied' })}</p>
                  </div>
                  <div className={`rounded-md border p-3 ${Math.max(0, (posDetail.headcount || 0) - (posDetail.occupied || 0)) > 0 ? 'bg-amber-50 border-amber-200' : ''}`}>
                    <p className="text-xl font-bold tabular-nums">{Math.max(0, (posDetail.headcount || 0) - (posDetail.occupied || 0))}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t('pages.orgBoard.vacancies', { defaultValue: 'Vacancies' })}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                    {t('pages.orgBoard.roleProfile', { defaultValue: 'Role profile' })}
                  </p>
                  {posDetail.role_profile ? (
                    <Badge variant="outline">{posDetail.role_profile.position_title || posDetail.role_profile.code}</Badge>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('pages.orgBoard.noRoleProfile', { defaultValue: 'No role profile linked.' })}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                    {t('pages.orgBoard.occupants', { defaultValue: 'Occupants' })}
                  </p>
                  {(posDetail.occupants || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('pages.orgBoard.noOccupants', { defaultValue: 'No occupants — open vacancy.' })}</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {posDetail.occupants.map((o) => (
                        <button key={o.id} type="button"
                                onClick={() => { setPersonDetail(o); }}
                                className="inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1 text-sm hover:bg-muted transition"
                                data-testid={`orgboard-occupant-${o.id}`}>
                          <UserRound className="h-3.5 w-3.5" /> {o.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Collaborator quick-view (canonical profile data — no duplicate system) */}
      <Dialog open={!!personDetail} onOpenChange={(o) => !o && setPersonDetail(null)}>
        <DialogContent className="sm:max-w-md" data-testid="orgboard-person-dialog">
          {personDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="h-9 w-9 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center justify-center">
                    {personDetail.avatar_initials || '—'}
                  </span>
                  {personDetail.name}
                </DialogTitle>
                <DialogDescription>
                  {t('pages.orgBoard.collaborator', { defaultValue: 'Collaborator quick view' })}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-base">
                {personDetail.position && (
                  <p className="flex items-center gap-2 text-sm">
                    <Briefcase className="h-4 w-4 text-muted-foreground" /> {personDetail.position}
                  </p>
                )}
                {personDetail.email && (
                  <p className="flex items-center gap-2 text-sm break-all">
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" /> {personDetail.email}
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
